import type { ReactNode } from 'react';
import type { PapierDeFiche, TypeDeFiche } from '@nextilt/commun';
import type { Bulle } from '../partage/api/types';
import { ChampsNotes } from './ChampsNotes';
import { DeplacerBulle } from './DeplacerBulle';
import type { ProprietesDeLaFiche } from './Fiche';
import { GestesDeLaFiche, MotDuPied } from './GestesDeLaFiche';
import { NavDeLaFiche } from './NavDeLaFiche';
import { SupprimerBulle } from '../projets';
import { TitreDeFiche } from './TitreDeFiche';
import type { MecaniqueDeLaFiche } from './useMecaniqueDeLaFiche';
import { teinteOuAccent } from '../apparence';

/**
 * La coque des deux fiches (chantier `nettoyage-fiche`, ticket 01) : l'écran transparent qui
 * referme d'un clic à côté, le panneau aux couleurs du projet, sa tête (« ‹ la mère »,
 * « 3 sur 6 ‹ › », la croix), puis, au-dessus, la confirmation de suppression et le choix de
 * la destination ; à l'ordinateur, la ligne du haut et le papier. Chaque fiche y range son contenu : l'ordinateur son panneau, le téléphone
 * ce qui tient sous le pouce.
 */
export function CoqueDeLaFiche({
  bulle,
  chemin,
  couleur,
  pouce = false,
  couchee = false,
  mecanique,
  onMere,
  onSoeur,
  onFermer,
  onSupprimee,
  onDeplacee,
  haut,
  papier,
  type,
  sansNav = false,
  enPage = false,
  children,
}: Pick<ProprietesDeLaFiche, 'bulle' | 'chemin' | 'couleur' | 'onMere' | 'onSoeur' | 'onFermer' | 'onSupprimee' | 'onDeplacee'> & {
  /** La fiche du téléphone, rangée pour le pouce. */
  pouce?: boolean;
  /** La fiche du téléphone couché, pleine à côté du rail (chantier telephone-fiche, 02). */
  couchee?: boolean;
  mecanique: MecaniqueDeLaFiche;
  /**
   * La ligne du haut (« Type ▾ », « Réglages », la croix ; chantier types-de-fiche, modification
   * 02 ; au téléphone, l'en-tête de la fiche, telephone-fiche 01). Avec elle, la croix quitte la
   * ligne de la mère et des sœurs.
   */
  haut?: ReactNode;
  /** Le papier de la fiche : son allure (`Fiche.css`, `.fiche--papier-*` ; au téléphone `.fiche--pouce-papier-*`). */
  papier?: PapierDeFiche;
  /** Le type de la fiche : ce qu'elle met en avant (`.fiche--type-*` ; au téléphone `.fiche--pouce-type-*`). */
  type?: TypeDeFiche;
  /**
   * Sans « ‹ la mère » ni « 3 sur 6 » : le panneau Réglages de l'ordinateur a sa propre tête
   * (reglages-de-la-fiche, deuxième passe de 02).
   */
  sansNav?: boolean;
  /**
   * La fiche en page, au téléphone dans un projet (chantier telephone-projet, 04) : posée dans la
   * page sous le fil, ni écran qui la referme, ni « ‹ la mère » ni sœurs (le fil les remplace).
   */
  enPage?: boolean;
  children: ReactNode;
}) {
  const classes = [
    'fiche',
    pouce ? 'fiche--pouce' : null,
    couchee ? 'fiche--couchee' : null,
    enPage ? 'fiche--en-page' : null,
    // Le téléphone a son propre dessin des papiers (`FicheAuPouce.css`, chantier telephone-fiche, 01).
    papier ? (pouce ? `fiche--pouce-papier-${papier}` : `fiche--papier-${papier}`) : null,
    type ? (pouce ? `fiche--pouce-type-${type}` : `fiche--type-${type}`) : null,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <>
      {/*
       * La planche ne pose AUCUN voile : la carte reste éclairée derrière le tiroir, elle
       * perd seulement son accent. Assombrir dramatiserait un geste qui n'a rien de
       * dramatique — on range une chose. L'écran reste là, transparent, pour que cliquer à
       * côté referme.
       */}
      {!enPage && (
        <div className={`fiche-ecran${couchee ? ' fiche-ecran--couchee' : ''}`} onClick={onFermer} aria-hidden="true" />
      )}

      <aside
        className={classes}
        data-tuto="fiche"
        role={enPage ? undefined : 'dialog'}
        aria-label={`fiche de ${bulle.titre}`}
        style={{ ['--teinte' as string]: teinteOuAccent(couleur) }}
      >
        {haut}
        {!sansNav && !enPage && (
          <NavDeLaFiche
            bulle={bulle}
            chemin={chemin}
            onMere={onMere}
            onSoeur={onSoeur}
            onFermer={onFermer}
            avecCroix={!haut}
          />
        )}
        {children}
      </aside>

      {mecanique.aSupprimer && <SupprimerBulle bulle={bulle} onGarder={mecanique.garder} onSupprimee={onSupprimee} />}

      {mecanique.aDeplacer && <DeplacerBulle bulle={bulle} onRenoncer={mecanique.nePasDeplacer} onDeplacee={onDeplacee} />}
    </>
  );
}

/**
 * Le titre, qu'on touche pour le renommer ; l'ordinateur y ajoute son crayon (`children`).
 */
export function TeteDeLaFiche({
  bulle,
  mecanique,
  onChanger,
  children,
}: {
  bulle: Bulle;
  mecanique: MecaniqueDeLaFiche;
  onChanger: (champs: Partial<Bulle>) => void;
  children?: ReactNode;
}) {
  return (
    <div className="fiche-tete">
      <TitreDeFiche
        titre={bulle.titre}
        edition={mecanique.renommer}
        onEdition={mecanique.setRenommer}
        onRenommer={(titre) => onChanger({ titre })}
      />
      {children}
    </div>
  );
}

/** Les notes, remises à neuf pour chaque bulle : un brouillon ne passe pas d'une bulle à l'autre. */
export function NotesDeLaFiche({ bulle, onChanger }: { bulle: Bulle; onChanger: (champs: Partial<Bulle>) => void }) {
  return <ChampsNotes key={`notes-${bulle.id}`} valeur={bulle.notes} onChanger={(notes) => onChanger({ notes })} />;
}

/**
 * Les gestes du pied, « C'est fait », « J'attends un retour » ou ceux de son état, puis le mot
 * de son état ; l'ordinateur glisse « Creuser » entre les deux (`children`). Ce que fait
 * « J'attends un retour » est à chaque fiche : l'ordinateur l'écrit aussitôt, le téléphone
 * demande d'abord le délai.
 */
export function GestesDuPied({
  bulle,
  annulation,
  onValider,
  onAttendre,
  onDefaire,
  children,
}: Pick<ProprietesDeLaFiche, 'bulle' | 'annulation' | 'onValider' | 'onDefaire'> & {
  onAttendre: () => void;
  children?: ReactNode;
}) {
  return (
    <>
      <GestesDeLaFiche
        bulleId={bulle.id}
        etat={bulle.etat}
        annulation={annulation}
        onValider={onValider}
        onAttendre={onAttendre}
        onDefaire={onDefaire}
      />
      {children}
      <MotDuPied etat={bulle.etat} annulation={annulation} />
    </>
  );
}
